import React from 'react';
import { useContextCacher } from 'common/utils/context';
import { Field, FieldTypes } from 'hellospa/components/editor/types';
import { PREPARER } from '../constants';
import Signature from './signature';
import Checkbox from './checkbox';
import Dropdown, { Props as DropdownProps } from './dropdown';
import Radio, { Props as RadioProps } from './radio';
import DateField from './date';
import Text from './text';
import Hyperlink from './hyperlink';

const NOOP = () => {};

const unreachable = (_t: never) => {};

export type WrapperStyles = React.CSSProperties & {
  height: string,
  width: string,
}
type FieldContext = {
  fieldData: Field,
  wrapperStyles: WrapperStyles,
  height: string,
  width: string,

}


type ProviderProps = {
  fieldData: Field,
  wrapperStyles: WrapperStyles,
  children: React.ReactNode,
}

const fieldContext = React.createContext<FieldContext>({} as FieldContext);

export function FieldContextProvider({ fieldData, wrapperStyles, children }: ProviderProps) {
  const contextCache = useContextCacher<FieldContext>();
  return (
    <fieldContext.Provider value={contextCache({
      fieldData,
      height: wrapperStyles.height,
      width: wrapperStyles.width,
      wrapperStyles,
    })}
    >
      {children}
    </fieldContext.Provider>
  );
}


// This is a SUPER awkward API and I wish I hadn't written it this way.
// Depending on which type of Field you have, props needs to be the matching
// props for  its implementatoin from the `componentMap`.
export type Props =
  | { fieldData: Field }
  | Partial<RadioProps>
  | Partial<DropdownProps>

type ImperitiveAPI = {
 focus(): void,
 // This API is forwarded from an implementation in `componentMap`. Not every
 // implementation provides this measure function.
 measure?: () => { height: number, width: number }
}


// This whole component's concept was a mistake. It got created while attempting
// to separate code that was formerly just Editor into code to be shared across
// Editor, Signer, and Overlay. Before TypeScript this used a `componentMap`
// object where the keys were field type and the values were the specific
// component implemenation. I fought with trying to get TypeScript to understand
// this and eventually had to give up. Now that object is gone and we just have
// a huge switch statement where every component gets the same props.
function DisplayField(props: Props, ref: React.Ref<ImperitiveAPI>) {
  const { fieldData, height, width, wrapperStyles } = React.useContext(fieldContext);
  const readOnly = fieldData.readOnly || false;

  // The caller can override the field data. The Editor may use this to show a
  // draft version of the field, or to replace the `.value` with the `.name`
  // for display purposes.
  const fd = props.fieldData || fieldData;

  let propOptions: any = {
    'data-qa-ref': fd.type === 'checkbox' ? fd.type : `${fd.type}-input`,
    ...props,
  };

  // For readOnly Fields we disabled all actions
  if (readOnly) {
    // setting fieldData.require to false
    fd.required = false;

    propOptions = {
      ...propOptions,
      disabled: true,
      onKeyPress: NOOP,
      onTouchEnd: NOOP,
      onClick: NOOP,
      onChange: NOOP,
      tabIndex: -1,
    };
  }

  let fieldImpl;
  switch (fd.type) {
    case FieldTypes.Signature:
      fieldImpl = (
        <Signature
          fieldData={fd}
          {...propOptions}
          width={width}
          height={height}
          ref={ref} />
      );
      break;
    case FieldTypes.Initials:
      fieldImpl = (
        <Signature
          fieldData={fd}
          {...propOptions}
          width={width}
          height={height}
          ref={ref} />
      );
      break;
    case FieldTypes.Text:
      fieldImpl = (
        <Text
          fieldData={fd}
          {...propOptions}
          width={width}
          height={height}
          ref={ref} />
      );
      break;
    case FieldTypes.Date:

      if (fd.signer === PREPARER) {
        fieldImpl = (
          <Text
            fieldData={fd}
            {...propOptions}
            width={width}
            height={height}
            ref={ref} />
        );
      } else {
        fieldImpl = (
          <DateField
            fieldData={fd}
            {...propOptions}
            width={width}
            height={height}
            ref={ref} />
        );
      }
      break;
    case FieldTypes.Checkbox:
      fieldImpl = (
        <Checkbox
          fieldData={fd}
          {...propOptions}
          width={width}
          height={height}
          ref={ref} />
      );
      break;
    case FieldTypes.Dropdown:
      fieldImpl = (
        <Dropdown
          fieldData={fd}
          {...propOptions}
          width={width}
          height={height}
          ref={ref} />
      );
      break;
    case FieldTypes.Radio:
      fieldImpl = (
        <Radio
          fieldData={fd}
          {...propOptions}
          width={width}
          height={height}
          ref={ref} />
      );
      break;
    case FieldTypes.Hyperlink:
      fieldImpl = (
        <Hyperlink
          fieldData={fd}
          {...propOptions}
          width={width}
          height={height}
          ref={ref} />
      );
      break;
    default:
      unreachable(fd);
  }

  return (
    <div style={wrapperStyles}>
      {fieldImpl}
    </div>
  );
}

export default React.forwardRef(DisplayField);
